import { getLuminance, getScale, toHex } from "color2k";
import type { ColorKey, ThemeBackground } from "./types";
import { DEFAULT_THEME, colorKeys } from "./types";
import type { MessengerConfig } from "@deskpro-product/messenger-api-schemas/messenger/config";

/**
 * Given a valid hex color, returns a color scale of 10 colors
 * The configActionColor becomes the 500 color
 */
export function getContrastTextColors(configActionColor: string): { light: string; dark: string } {
  if (configActionColor === DEFAULT_THEME.light.actionColor) {
    return {
      light: DEFAULT_THEME.light.contrastTextColor,
      dark: DEFAULT_THEME.dark.contrastTextColor,
    };
  }

  try {
    return getLuminance(configActionColor) > 0.5
      ? {
          light: DEFAULT_THEME.light.contrastTextColor,
          dark: DEFAULT_THEME.dark.contrastTextColor,
        }
      : {
          light: DEFAULT_THEME.dark.contrastTextColor,
          dark: DEFAULT_THEME.light.contrastTextColor,
        };
  } catch (e) {
    console.error(`Invalid color: ${configActionColor}`, e);
    return {
      light: DEFAULT_THEME.light.contrastTextColor,
      dark: DEFAULT_THEME.dark.contrastTextColor,
    };
  }
}

/**
 * Given a valid hex color, returns a color scale of 10 colors
 * The configActionColor becomes the 500 color
 */
export function getColorScales(configActionColor: string): {
  light: Record<ColorKey, string>;
  dark: Record<ColorKey, string>;
} {
  if (configActionColor === DEFAULT_THEME.light.actionColor) {
    return {
      light: DEFAULT_THEME.light.actionColorScale,
      dark: DEFAULT_THEME.dark.actionColorScale,
    };
  }

  try {
    const light = getColorScale(configActionColor);
    const dark = getColorScale(light[700]);

    return {
      light,
      dark,
    };
  } catch (e) {
    console.error(`Invalid color: ${configActionColor}`, e);
    return {
      light: DEFAULT_THEME.light.actionColorScale,
      dark: DEFAULT_THEME.dark.actionColorScale,
    };
  }
}

function getColorScale(configActionColor: string): Record<ColorKey, string> {
  try {
    const lighterColors = getColorScaleValues(7, "white", configActionColor);
    const darkerColors = getColorScaleValues(6, configActionColor, "black");

    const colorScale = [...lighterColors.slice(1), ...darkerColors.slice(1, -1)];
    if (colorScale.length !== colorKeys.length) {
      throw new Error(`Invalid color scale length: ${colorScale.length}`);
    }

    // Remove the first color from the light colors (white),
    // Remove the first color from the dark colors (it duplicates the configActionColor from the light colors)
    // and the last color from the dark colors (black)
    return colorKeys.reduce(
      (acc, key, i) => {
        acc[key] = colorScale[i];
        return acc;
      },
      { ...DEFAULT_THEME.light.actionColorScale },
    );
  } catch (e) {
    console.error(`Invalid color: ${configActionColor}`, e);
    return DEFAULT_THEME.light.actionColorScale;
  }
}

export const getActionColor = (config: MessengerConfig): string => {
  return config.settings?.themeSettings?.actionColour ?? DEFAULT_THEME.light.actionColor;
};

export const getLauncherButtonColor = (config: MessengerConfig): string => {
  return config?.settings?.launcherSettings?.color ?? DEFAULT_THEME.light.launcherButtonColor;
};

export const getBackground = (config: MessengerConfig): ThemeBackground => {
  const backgroundConfig = config.settings?.themeSettings?.background;
  const textColor = backgroundConfig?.textColor ?? DEFAULT_THEME.light.background.textColor;
  const color =
    backgroundConfig?.name === "color" && backgroundConfig.bgColor
      ? backgroundConfig.bgColor
      : DEFAULT_THEME.light.background.color;
  const bgGradient = backgroundConfig?.name === "gradient" ? backgroundConfig : undefined;

  return {
    color,
    textColor,
    bgStart: bgGradient?.bgStart,
    bgEnd: bgGradient?.bgEnd,
  };
};

/** Returns an array of 'count' colors equally spaced across the range of colours between `from` and `to`, inclusive of `from` and `to`  */
const getColorScaleValues = (count: number, from: string, to: string) => {
  const delta = 1 / (count - 1);
  const scaleValues = Array.from({ length: count }, (_, i) => i * delta);
  return scaleValues.map(getScale(from, to)).map(toHex);
};
